import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import RichTextEditor from '../components/RichTextEditor';
import { useAuth } from '../contexts/AuthContext';
import { 
  FileText, Plus, Trash2, Edit2, Save, X, Copy, 
  ChevronDown, ChevronRight, BookOpen, Search,
  LayoutTemplate, FileCheck, Briefcase, Users, Shield
} from 'lucide-react';
import { sanitizeHTML } from '../lib/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Section type options with icons
const SECTION_TYPES = [
  { value: 'cover', label: 'Cover Page', icon: LayoutTemplate },
  { value: 'about', label: 'About / Intro', icon: BookOpen },
  { value: 'benefits', label: 'Benefits', icon: FileCheck },
  { value: 'investment', label: 'Investment / Pricing', icon: Briefcase },
  { value: 'terms', label: 'Terms & Conditions', icon: Shield },
  { value: 'agreement', label: 'Agreement', icon: FileText },
  { value: 'custom', label: 'Custom', icon: Users },
];

const SectionTemplatesManager = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  // Edit/Create modal state
  const [showModal, setShowModal] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    section_type: 'custom'
  });
  const [saving, setSaving] = useState(false);
  
  // Expanded preview state
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/library-sections`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSections(data);
      }
    } catch (error) {
      console.error('Failed to fetch sections:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingSection(null);
    setFormData({ title: '', content: '<p>Enter your section content here...</p>', section_type: 'custom' });
    setShowModal(true);
  };

  const openEditModal = (section) => {
    setEditingSection(section);
    setFormData({
      title: section.title || '',
      content: section.content || '',
      section_type: section.section_type || 'custom'
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSection(null);
    setFormData({ title: '', content: '', section_type: 'custom' });
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      alert('Please enter a section title');
      return;
    }

    setSaving(true);
    try {
      const url = editingSection 
        ? `${BACKEND_URL}/api/library-sections/${editingSection.section_id}`
        : `${BACKEND_URL}/api/library-sections`;
      
      const response = await fetch(url, {
        method: editingSection ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        closeModal();
        fetchSections();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save section');
      }
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save section');
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async (section) => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/library-sections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: `${section.title} (Copy)`,
          content: section.content,
          section_type: section.section_type
        })
      });

      if (response.ok) {
        fetchSections();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to duplicate section');
      }
    } catch (error) {
      console.error('Duplicate error:', error);
      alert('Failed to duplicate section');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (sectionId) => {
    if (!isAdmin) {
      alert('Only admins can delete section templates');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this section template? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/library-sections/${sectionId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        fetchSections();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete section');
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete section');
    }
  };

  const getTypeInfo = (typeValue) => {
    return SECTION_TYPES.find(t => t.value === typeValue) || SECTION_TYPES[SECTION_TYPES.length - 1];
  };

  // Filter sections
  const filteredSections = sections.filter(section => {
    const matchesSearch = section.title?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || section.section_type === filterType;
    return matchesSearch && matchesType;
  });

  // Group sections by type
  const groupedSections = filteredSections.reduce((acc, section) => {
    const type = section.section_type || 'custom';
    if (!acc[type]) acc[type] = [];
    acc[type].push(section);
    return acc;
  }, {});

  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = sanitizeHTML(html || '');
    return tmp.textContent || tmp.innerText || '';
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-[#A2FF00]">Loading...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              <BookOpen className="w-7 h-7 text-[#A2FF00]" />
              Section Templates
            </h1>
            <p className="text-gray-400 mt-1">
              Reusable content blocks for your proposals
            </p>
          </div>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-4 py-2 bg-[#A2FF00] text-black font-bold rounded-sm hover:bg-[#8FE600] transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Section
          </button>
        </div>

        {/* Search and Filter Bar */}
        <div className="flex gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search sections..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-[#111] border border-white/20 rounded-sm pl-10 pr-4 py-2 text-white placeholder-gray-500 focus:border-[#A2FF00] outline-none"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="bg-[#111] border border-white/20 rounded-sm px-4 py-2 text-white focus:border-[#A2FF00] outline-none"
          >
            <option value="all">All Types</option>
            {SECTION_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-[#111] border border-white/10 rounded-sm p-4">
            <div className="text-2xl font-bold text-[#A2FF00]">{sections.length}</div>
            <div className="text-gray-400 text-sm">Total Sections</div>
          </div>
          {SECTION_TYPES.slice(0, 3).map(type => {
            const count = sections.filter(s => s.section_type === type.value).length;
            return (
              <div key={type.value} className="bg-[#111] border border-white/10 rounded-sm p-4">
                <div className="text-2xl font-bold text-white">{count}</div>
                <div className="text-gray-400 text-sm">{type.label}</div>
              </div>
            );
          })}
        </div>

        {/* Sections List */}
        {filteredSections.length === 0 ? (
          <div className="bg-[#111] border border-white/10 rounded-sm p-12 text-center">
            <BookOpen className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-white font-medium mb-2">No sections found</h3>
            <p className="text-gray-500 mb-4">
              {searchQuery || filterType !== 'all' 
                ? 'Try adjusting your search or filter'
                : 'Create your first section template to get started'}
            </p>
            {!searchQuery && filterType === 'all' && (
              <button
                onClick={openCreateModal}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#A2FF00] text-black font-bold rounded-sm hover:bg-[#8FE600] transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create Section
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {Object.entries(groupedSections).map(([type, typeSections]) => {
              const typeInfo = getTypeInfo(type);
              const TypeIcon = typeInfo.icon;
              
              return (
                <div key={type} className="bg-[#111] border border-white/10 rounded-sm overflow-hidden">
                  {/* Type Header */}
                  <div className="px-4 py-2 bg-white/5 border-b border-white/10 flex items-center gap-2">
                    <TypeIcon className="w-4 h-4 text-[#A2FF00]" />
                    <span className="text-white font-medium">{typeInfo.label}</span>
                    <span className="text-gray-500 text-sm">({typeSections.length})</span>
                  </div>
                  
                  {/* Sections */}
                  <div className="divide-y divide-white/5">
                    {typeSections.map((section) => (
                      <div key={section.section_id} className="group">
                        {/* Section Row */}
                        <div className="flex items-center gap-4 p-4 hover:bg-white/5 transition-colors">
                          <button
                            onClick={() => setExpandedId(expandedId === section.section_id ? null : section.section_id)}
                            className="text-gray-400 hover:text-white"
                          >
                            {expandedId === section.section_id 
                              ? <ChevronDown className="w-4 h-4" />
                              : <ChevronRight className="w-4 h-4" />
                            }
                          </button>
                          
                          <div className="flex-1 min-w-0">
                            <h3 className="text-white font-medium truncate">{section.title}</h3>
                            <p className="text-gray-500 text-sm truncate">
                              {stripHtml(section.content).substring(0, 100)}...
                            </p>
                          </div>
                          
                          {/* Actions - Always visible */}
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => openEditModal(section)}
                              className="p-2 text-gray-400 hover:text-[#A2FF00] hover:bg-white/10 rounded-sm transition-colors"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDuplicate(section)}
                              className="p-2 text-gray-400 hover:text-blue-400 hover:bg-white/10 rounded-sm transition-colors"
                              title="Duplicate"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            {isAdmin && (
                              <button
                                onClick={() => handleDelete(section.section_id)}
                                className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-sm transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                        
                        {/* Expanded Preview */}
                        {expandedId === section.section_id && (
                          <div className="px-4 pb-4 pl-12">
                            <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4">
                              <div 
                                className="prose prose-invert prose-sm max-w-none"
                                dangerouslySetInnerHTML={{ __html: sanitizeHTML(section.content || '<p>No content</p>') }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#0A0A0A] border border-white/20 rounded-sm w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="p-4 border-b border-white/20 flex items-center justify-between bg-[#111]">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  {editingSection ? <Edit2 className="w-5 h-5 text-[#A2FF00]" /> : <Plus className="w-5 h-5 text-[#A2FF00]" />}
                  {editingSection ? 'Edit Section Template' : 'Create Section Template'}
                </h2>
                <button
                  onClick={closeModal}
                  className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-sm transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Modal Body */}
              <div className="flex-1 overflow-auto p-4 space-y-4">
                {/* Title & Type */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Section Title</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="e.g., About Our Company"
                      className="w-full bg-[#111] border border-white/20 rounded-sm px-3 py-2 text-white placeholder-gray-500 focus:border-[#A2FF00] outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Section Type</label>
                    <select
                      value={formData.section_type}
                      onChange={(e) => setFormData({ ...formData, section_type: e.target.value })}
                      className="w-full bg-[#111] border border-white/20 rounded-sm px-3 py-2 text-white focus:border-[#A2FF00] outline-none"
                    >
                      {SECTION_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {/* Content Editor */}
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Content</label>
                  <RichTextEditor
                    content={formData.content}
                    onChange={(content) => setFormData({ ...formData, content })}
                    placeholder="Enter your section content..."
                  />
                </div>
              </div>
              
              {/* Modal Footer */}
              <div className="p-4 border-t border-white/20 flex justify-end gap-3 bg-[#111]">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2 bg-[#A2FF00] text-black font-bold rounded-sm hover:bg-[#8FE600] transition-colors disabled:opacity-50"
                >
                  {saving ? (
                    <>Saving...</>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {editingSection ? 'Save Changes' : 'Create Section'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default SectionTemplatesManager;
